import React from 'react';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Autocomplete, Box, IconButton, TextField, Typography, useTheme } from '@mui/material';

import CustomButton from 'components/Button';
import DatePicker from 'components/DatePicker';
import { FilterButtons } from 'components/GlobalStyles/styles';
import { MessageType } from 'components/SnackBar';
import { UserType } from 'types/WorkLogTypes';

import {
  SyncTaskStart,
  SyncTaskStatus,
  useGetUserListQuery,
  useLazyAdjustTimeSheetsPollingQuery,
  useLazyAdjustTimeSheetsQuery,
  useLazySyncTimeSheetsPollingQuery,
  useLazySyncTimeSheetsQuery,
  useLazySyncWorklogsToMYOBPollingQuery,
  useSyncWorklogsToMYOBMutation,
} from '../../../../services/busmanApi';
import {
  getApiErrorMessage,
  getDayLogLine,
  getLastDay,
  getPercent,
  getResultLogLine,
  getTaskOutcome,
  getTaskProgressMessage,
  pollSyncTask,
  PollTrigger,
  taskTimeoutForDays,
} from '../../hooks/syncTask';

import { Header, ModalPageContainer } from './styles';

export interface WorklogsSyncProgress {
  percent: number;
  message: string;
}

// Reports how far through a single step we are, as a 0-1 fraction
type StepProgress = (fraction: number, message: string) => void;

interface SyncConfirmProps {
  onClose: () => void;
  open?: boolean;
  setMessage: (message: MessageType | null) => void;
  setLogMessage: (type: string, message: string) => void;
  setWorklogsSyncing: (value: boolean) => void;
  setWorklogsProgress: (progress: WorklogsSyncProgress | null) => void;
}

const defaultOption = { user_id: '', user_role: '', username: '', full_name: 'All' };

const SyncWorklogsModal = ({
  onClose,
  open = false,
  setMessage,
  setLogMessage,
  setWorklogsSyncing,
  setWorklogsProgress,
}: SyncConfirmProps) => {
  const theme = useTheme();
  const [errorMsg, setErrorMsg] = React.useState<string>('');
  const [fromDate, setFromDate] = React.useState<Dayjs | null>(dayjs().subtract(1, 'week'));
  const [toDate, setToDate] = React.useState<Dayjs | null>(dayjs());
  const [selectedEmployee, setSelectedEmployee] = React.useState<UserType | null>(defaultOption);
  const [syncWorklogsToMYOB] = useSyncWorklogsToMYOBMutation();
  const [pollWorklogsSync] = useLazySyncWorklogsToMYOBPollingQuery();
  const [syncTimeSheets] = useLazySyncTimeSheetsQuery();
  const [pollTimeSheetsSync] = useLazySyncTimeSheetsPollingQuery();
  const [adjustTimeSheets] = useLazyAdjustTimeSheetsQuery();
  const [pollAdjustTimeSheets] = useLazyAdjustTimeSheetsPollingQuery();
  dayjs.extend(isSameOrAfter);

  const { data: userList = [] } = useGetUserListQuery({});

  const userOptions = [defaultOption, ...userList];

  // Both outright failures and skips land here: a skipped task never ran, because another
  // sync held the lock, so it must not reach the user as a completed step.
  const reportFailure = (message: string) => {
    setLogMessage('error', message);
    setMessage({ type: 'error', msg: message });
  };

  /**
   * All three MYOB steps run as a single task over the whole date range for every
   * employee, so there is no per employee or per date loop here - each is started once
   * and followed through to a terminal state. Returns false when the step did not
   * complete, which includes a task that was skipped because another sync held the lock.
   */
  const runSyncTask = async <TArgs extends object>(
    label: string,
    startTask: (args: TArgs) => Promise<{ data?: SyncTaskStart; error?: unknown }>,
    pollTask: PollTrigger,
    params: TArgs,
    timeoutMs: number,
    onStepProgress: StepProgress,
  ): Promise<boolean> => {
    onStepProgress(0, label);

    const result = await startTask(params);

    if ('error' in result) {
      reportFailure(getApiErrorMessage(result.error, `Error ${label}`));
      return false;
    }

    const taskId = result.data?.task_id;
    if (!taskId) {
      // The endpoint answered with a finished payload - there is nothing to poll
      setMessage({ type: 'success', msg: `Finished ${label}` });
      return true;
    }

    let lastDayKey = '';
    let status: SyncTaskStatus;

    try {
      status = await pollSyncTask(
        pollTask,
        taskId,
        (taskStatus) => {
          onStepProgress(getPercent(taskStatus) / 100, getTaskProgressMessage(taskStatus, label));

          // Log each date as the sweep finishes with it, so the panel shows what is
          // happening rather than going quiet for minutes. A date that fails does not
          // stop the task, so those are surfaced the same way.
          const day = getLastDay(taskStatus);
          const dayKey = `${day?.date}-${day?.processed ?? day?.adjusted}-${day?.message}`;
          if (day && dayKey !== lastDayKey) {
            lastDayKey = dayKey;
            setLogMessage(day.sync_success === false ? 'error' : 'success', getDayLogLine(day, label));
          }
        },
        timeoutMs,
      );

      const outcome = getTaskOutcome(status, label);
      if (outcome.type !== 'success') {
        reportFailure(outcome.message);
        return false;
      }
    } catch (error) {
      reportFailure(`Lost track of ${label}`);
      return false;
    }

    setLogMessage('success', getResultLogLine(status, label));
    setMessage({ type: 'success', msg: `Finished ${label}` });
    return true;
  };

  const handleSyncing = async () => {
    if (!selectedEmployee) {
      setErrorMsg('Please select an employee');
      return;
    }

    if (!fromDate || !toDate) {
      setErrorMsg('Please select a valid date range');
      return;
    }

    if (!toDate.startOf('day').isSameOrAfter(fromDate.startOf('day'))) {
      setErrorMsg('FROM Date cannot be after TO Date');
      return;
    }

    onClose();
    setWorklogsSyncing(true);
    setLogMessage('success', 'Syncing Worklogs...');

    const startDate = fromDate.format('YYYY-MM-DD');
    const endDate = toDate.format('YYYY-MM-DD');
    // All three tasks sweep every employee unless one was explicitly picked. The push
    // names it `user_id` in its body, the two payroll endpoints `employee_id` in the
    // query string, but all three mean the same EEAEmployee.
    const employeeId = selectedEmployee.full_name !== 'All' ? selectedEmployee.user_id : undefined;
    // Runtime is bounded by the number of dates swept, not by headcount
    const days = toDate.startOf('day').diff(fromDate.startOf('day'), 'day') + 1;
    const timeoutMs = taskTimeoutForDays(days);

    /**
     * One task per step, each sweeping the whole range. They run in order and the chain
     * stops at the first step that does not complete: the adjustment only makes sense
     * once the push has marked the worklogs SYNCED, and a skipped step means another
     * sync holds that endpoint's lock and nothing was done at all.
     */
    const steps: Array<(onStepProgress: StepProgress) => Promise<boolean>> = [
      (onStepProgress) =>
        runSyncTask(
          'syncing worklogs',
          syncWorklogsToMYOB,
          pollWorklogsSync,
          { start_date: startDate, end_date: endDate, user_id: employeeId },
          timeoutMs,
          onStepProgress,
        ),
      (onStepProgress) =>
        runSyncTask(
          'syncing timesheets',
          syncTimeSheets,
          pollTimeSheetsSync,
          { start_date: startDate, end_date: endDate, employee_id: employeeId },
          timeoutMs,
          onStepProgress,
        ),
      (onStepProgress) =>
        runSyncTask(
          'adjusting timesheets',
          adjustTimeSheets,
          pollAdjustTimeSheets,
          { start_date: startDate, end_date: endDate, employee_id: employeeId },
          timeoutMs,
          onStepProgress,
        ),
    ];

    const totalSteps = steps.length;
    let completedSteps = 0;
    const reportProgress: StepProgress = (fraction, message) =>
      setWorklogsProgress({ percent: Math.round(((completedSteps + fraction) / totalSteps) * 100), message });

    setWorklogsProgress({ percent: 0, message: 'Starting worklog sync...' });

    try {
      for (const runStep of steps) {
        const completed = await runStep(reportProgress);
        if (!completed) {
          setLogMessage('error', 'Sync stopped - the remaining steps were not run');
          break;
        }
        completedSteps += 1;
      }

      if (completedSteps === totalSteps) {
        setMessage({ type: 'success', msg: 'Worklog Sync Completed' });
        setLogMessage('success', 'Syncing Worklogs Complete');
      }
    } catch (error) {
      setLogMessage('error', 'Error Syncing Worklogs');
      setMessage({ type: 'error', msg: 'Sync did not complete' });
    }
    setWorklogsSyncing(false);
    setWorklogsProgress(null);
  };

  const handleSelectEmployee = (value: UserType | null) => {
    setSelectedEmployee(value);
    if (errorMsg) setErrorMsg('');
  };

  const handleFromDateChange = (value: Dayjs | null) => {
    setFromDate(value);
    const diff = value && Math.abs(value?.diff(dayjs(toDate), 'day'));
    if (diff && diff > 7) {
      let newToDate = dayjs(value).add(1, 'week');
      newToDate = newToDate.isSameOrBefore(dayjs()) ? newToDate : dayjs(); // to date should exceed current date
      setToDate(newToDate);
    }
  };

  const handleToDateChange = (value: Dayjs | null) => {
    setToDate(value);
    const diff = value && Math.abs(value?.diff(dayjs(fromDate), 'day'));
    if (diff && diff > 7) setFromDate(dayjs(value).subtract(1, 'week'));
  };

  return (
    <ModalPageContainer open={open} onClose={onClose}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.paper,
          // height: '300px',
          maxWidth: '608px',
          width: '100%',
        }}>
        <Header>
          <Typography variant="h4">Sync Worklogs from MYOB to Busmin</Typography>
          <IconButton onClick={onClose} edge="end">
            <ClearIcon />
          </IconButton>
        </Header>
        <Box sx={{ display: 'flex', flexDirection: 'column', padding: theme.spacing(2) }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              gap: 2,
              width: '100%',
              [theme.breakpoints.down('sm')]: {
                flexDirection: 'column',
              },
            }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Typography variant="body2">FROM</Typography>
              <DatePicker allowMaxDate onChange={handleFromDateChange} value={fromDate} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
              <Typography variant="body2">TO</Typography>
              <DatePicker onChange={handleToDateChange} value={toDate} />
            </Box>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              paddingTop: theme.spacing(2),
              width: '280px',
              [theme.breakpoints.down('sm')]: {
                width: '100%',
              },
            }}>
            <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
              Employee
            </Typography>
            <Autocomplete
              size="small"
              value={selectedEmployee}
              options={userOptions}
              getOptionLabel={(option) => `${option.full_name}`}
              isOptionEqualToValue={(option, value) => option.full_name === value.full_name}
              onChange={(event, value) => handleSelectEmployee(value)}
              ListboxProps={{
                style: {
                  fontSize: '12px',
                },
              }}
              popupIcon={<ExpandMoreIcon fontSize="small" />}
              renderInput={(params) => <TextField {...params} />}
            />
            {errorMsg && (
              <Typography
                variant="subtitle2_italic"
                sx={{ color: theme.palette.busmanColors.busmanRed, margin: theme.spacing(1) }}>
                {errorMsg}
              </Typography>
            )}
          </Box>
          <FilterButtons sx={{ marginLeft: 'auto' }}>
            <CustomButton variant="contained" width="medium" text="Sync Worklogs" onClick={handleSyncing} />
          </FilterButtons>
        </Box>
      </Box>
    </ModalPageContainer>
  );
};

export default SyncWorklogsModal;
